import {createStore, filterNil} from '@ngneat/elf';
import {selectEntity, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {FeilhåndteringArgumenter, registrerEntitet, serverFraLagerOgRequestPakketIMeta, serverFraRequest, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentId, DokumentSignatarerModell, DokumentSignatarModell, DokumentsigneringRessursClient, DokumentTilSigneringModell, DokumentVersjonModell, GruppeMedlemListeModell, LitenIkkeTomStreng, MedlemId, OppdaterSignatarerSpesifikasjon, OtpSigneringValideringResponsModell, PotensiellDokumentSignatarModell, SendTilSigneringSpesifikasjon, SignatarValideringsfeil, SigneringStatus, TofaktorSigneringskodeKanal, ValideringsresultatModell} from "../ressurser/generertNg2HttpKlient";
import {firstValueFrom, merge, Observable, of, switchMap} from "rxjs";
import {HttpResponse} from "@angular/common/http";
import {map, tap} from "rxjs/operators";
import {erArkivdokument, erSaksdokument} from "felles-ng2/modell/dokumentModellHjelpere";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {finnMedlemnavn} from "felles-ng2/modell/medlemModellHjelpere";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {DataOgMeta, kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {ArkivDatatjeneste} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {BANKID_TERMINERENDE_STATUSER, SigneringBankIDQESTjeneste} from "web-ng2/moduler/signering/tjenester/signering-bankid-qes-service";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {potensiellDokumentSignatarSomIkkeSkalSignere} from "felles-ng2/modell/dokumentSignatarerModellHjelpere";

const debug = initDebugger('DokumentSigneringDatatjeneste');


/** Datalager for initiering og redigering av dokumentsignering, herunder {@link DokumentSignatarerModell}. */
//@todo lytte til dokument signering hendelser?
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class DokumentSigneringDatatjeneste {

    /** Lokalt datalager for {@link DokumentSignatarerModell} entiteter.
     * Merk at {@link DokumentSignatarerModell#dokumentId} benyttes som id i lageret. */
      //public for testbarhet
    readonly dokumentSignatarerLager = createStore({name: 'dokumentSignatarer'}, withEntities<DokumentSignatarerModell, 'dokumentId'>({idKey: 'dokumentId'}));

    constructor(private dokumentsigneringRessurs: DokumentsigneringRessursClient,
                private gruppeDatatjeneste: GruppeDatatjeneste,
                private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private arkivDatatjeneste: ArkivDatatjeneste,
                private bankidQES: SigneringBankIDQESTjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
    }

    /** Sørger for å relaste data påvirket av signeringsstatus, dvs signatarer og dokumentversjon */
    oppdaterSigneringsstatusILokalLager(dokumentversjon: DokumentVersjonModell) {
        this.relast(dokumentversjon.dokumentId);

        if (erSaksdokument(dokumentversjon)) this.saksdokumentDatatjeneste.relast(dokumentversjon.dokumentId);
        if (erArkivdokument(dokumentversjon)) this.arkivDatatjeneste.relastArkivFilnode(dokumentversjon.foreldre.arkivfilnodeId!);
    }

    /** Laster signatarer for angitt dokument fra server og oppdaterer lokalt datalager med disse. */
    async relast(id: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(id, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    hentMedMeta(dokumentId: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<DokumentSignatarerModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.dokumentsigneringRessurs.signatarer(dokumentId),
            registrerILager: respons => registrerEntitet(this.dokumentSignatarerLager, respons.body!),
            hentFraLager: () => this.dokumentSignatarerLager.pipe(selectEntity(dokumentId), filterNil()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente signatarer', 'hentMedMeta')
        });
    }

    hent(dokumentId: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<DokumentSignatarerModell> {
        return this.hentMedMeta(dokumentId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /**
     * Henter signatarinformasjon for aktiv bruker for et gitt dokument.
     * Lever oppdatert informasjon når signatarinformasjon i lokalt datalager blir oppdatert,
     * feks som følge av endringer i hvem som skal signere via kall til {@link #oppdaterSignatarer}
     */
    signatarinformasjonForAktivBruker(dokumentId: DokumentId): Observable<PotensiellDokumentSignatarModell> {
        //initielt ressurskall
        return this.dokumentsigneringRessurs.signatarinformasjonForAktivBruker(dokumentId).somObservable()
          .pipe(
            switchMap(potensiellDokumentSignatar =>
              merge(
                //lever verdi fra initielt ressurskall
                of(potensiellDokumentSignatar),
                //gjør nytt ressurskall når signatarinformasjon i lokallager oppdateres
                this.dokumentSignatarerLager.pipe(
                  selectEntity(dokumentId),
                  filterNil(),
                  map(signatarer => signatarer.signatarer.find(kandidat => kandidat.medlem.id == potensiellDokumentSignatar.signatar?.medlem.id) ?? null),
                  switchMap(signatar =>
                    //dersom medlem i potensiellDokumentSignatar skal ikke lenger skal signere dokumentet gjør vi ikke noe ressurskall
                    erNil(signatar) ?
                      of(potensiellDokumentSignatarSomIkkeSkalSignere()) :
                      this.dokumentsigneringRessurs.signatarinformasjonForAktivBruker(dokumentId).somObservable())
                ))));
    }

    sendTilSignering(dokumentversjon: DokumentVersjonModell, spesifikasjon: SendTilSigneringSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SendTilSigneringValideringsresultatDLM> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.dokumentsigneringRessurs.sendTilSignering(dokumentversjon.dokumentId, spesifikasjon).råRespons()
              .pipe(
                map(respons => respons as HttpResponse<SendTilSigneringValideringsresultatDLM>),
                tap(validersingsrespons => {
                    if (validersingsrespons.body!.harFeil) return;
                    this.oppdaterDokumentIRelaterteDatatjenester(dokumentversjon, {signeringStatus: SigneringStatus.tilSignering, aksepterteSignaturmetoder: spesifikasjon.signaturmetoder});
                    if (spesifikasjon.huskSignaturmetodeValg)
                        this.gruppeDatatjeneste.oppdaterStandardSignaturmetoderILokallager(dokumentversjon.gruppeId, spesifikasjon.signaturmetoder);
                    //det kunne vært aktuelt å oppdatere dokumentSignatarerLager men respons inneholder ikke tilstrekkelige data
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke sende dokument til signering')), 'sendTilSignering')
        }));
    }

    oppdaterSignatarer(dokumentversjon: DokumentVersjonModell, spesifikasjon: OppdaterSignatarerSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SendTilSigneringValideringsresultatDLM> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.dokumentsigneringRessurs.oppdaterSignatarer(dokumentversjon.dokumentId, spesifikasjon).råRespons()
              .pipe(
                map(respons => respons as HttpResponse<SendTilSigneringValideringsresultatDLM>),
                tap(validersingsrespons => {
                    if (validersingsrespons.body!.harFeil) return;
                    //siden signatarer-samlingen kan ha blitt oppdatert holder det ikke å kalle this.oppdaterSigneringsstatusILokalLager() - må relaste
                    this.oppdaterSigneringsstatusILokalLager(dokumentversjon);
                })
              ),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdaterer signeringsinformasjon')), 'oppdaterSignatarer')
        }));
    }

    async signerMedSmsOtp(dokumentversjon: DokumentVersjonModell, otp: string, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<OtpSigneringValideringResponsModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () =>
              this.dokumentsigneringRessurs.signerMedSmsOtp(dokumentversjon.dokumentId, {otp}).råRespons()
                .pipe(tap(() => this.oppdaterSigneringsstatusILokalLager(dokumentversjon))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke signere dokumentet')), 'signerMedSmsOtp')
        }));
    }

    async signerMedOtp(dokumentversjon: DokumentVersjonModell, otp: string, kanal: TofaktorSigneringskodeKanal, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<OtpSigneringValideringResponsModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () =>
              this.dokumentsigneringRessurs.signerMedOtp(dokumentversjon.dokumentId, {otp, kanal}).råRespons()
                .pipe(tap(() => this.oppdaterSigneringsstatusILokalLager(dokumentversjon))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke signere dokumentet')), 'signerMedOtp')
        }));
    }

    signerMedBankID(dokumentversjon: DokumentVersjonModell, signatar: DokumentSignatarModell) {
        return this.bankidQES.startBankIDSigneringPopup(dokumentversjon, signatar).pipe(tap((status) => {
            // Oppdater datalager når vi får en terminerende status, ellers ender vi med å gjøre mange kall om polling går lenge
            if (BANKID_TERMINERENDE_STATUSER.has(status)) {
                this.oppdaterSigneringsstatusILokalLager(dokumentversjon);
            }
        }))
    }

    kanseller(dokumentversjon: DokumentVersjonModell, årsak: LitenIkkeTomStreng, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<any> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () =>
              this.dokumentsigneringRessurs.låsOpp(dokumentversjon.dokumentId, {aarsak: årsak}).råRespons()
                .pipe(tap(() => {
                    this.oppdaterDokumentIRelaterteDatatjenester(dokumentversjon, {signeringStatus: SigneringStatus.ikkeAktivert});
                    this.meldingTilBruker.infomelding(new I18nTekst(i18n("Signering av dokumentet '{navn}' har blitt kanselert"), {navn: dokumentversjon.pdfFilnavn!}));
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke kansellere signeringen')), 'kanseller')
        }));
    }

    markerSomFerdigSignert(dokumentId: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<DokumentTilSigneringModell<DokumentVersjonModell>> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.dokumentsigneringRessurs.markerSomFerdigSignert(dokumentId).råRespons()
              .pipe(
                tap(respons => {
                    const signertDokument = respons.body!.tilSignering;
                    const signatarer = respons.body!.signatarer;
                    this.dokumentSignatarerLager.update(upsertEntities(signatarer))
                    this.oppdaterDokumentIRelaterteDatatjenester(signertDokument, signertDokument);
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdaterer signeringsinformasjon')), 'oppdaterSignatarer')
        }));
    }

    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private oppdaterDokumentIRelaterteDatatjenester(dokumentversjon: DokumentVersjonModell, oppdateringer: Partial<DokumentVersjonModell>) {
        if (erSaksdokument(dokumentversjon)) this.saksdokumentDatatjeneste.oppdaterILokallager(dokumentversjon.dokumentId, oppdateringer);
        if (erArkivdokument(dokumentversjon)) this.arkivDatatjeneste.oppdaterArkivFilnodeILokallager(dokumentversjon.foreldre.arkivfilnodeId!, filnode => ({...filnode, dokument: {...filnode.dokument, ...oppdateringer}}));
    }

}

/** typet versjon av {@link ValideringsresultatModell} */
export type SendTilSigneringValideringsresultatDLM = TypetValideringsresultatModell<SignatarValideringsfeil, { dokumentId: DokumentId; medlemId?: MedlemId; }>;

export function valideringsfeilSomTekst(valideringsresultat: TypetValideringsresultatModell<SignatarValideringsfeil>, medlemmer: GruppeMedlemListeModell[]): I18nTekst[] {
    return valideringsresultat.feil
      .map(f => {
          switch (f.feilkode) {
              case SignatarValideringsfeil.medlem_skjermet:
                  return new I18nTekst(i18n('{medlem} har ikke tilgang til dokumentet og kan ikke settes til å signere'), {medlem: finnMedlemnavn(medlemmer, f.data.medlemId) ?? f.data.medlemId});
            //følgende feil skal det ikke være mulig å generere via GUI
              case SignatarValideringsfeil.medlem_ikke_satt:
              case SignatarValideringsfeil.dokument_ikke_satt:
              case SignatarValideringsfeil.medlem_feil_gruppe:
              default:
                  return new I18nTekst(i18n("En feil har oppstått: kode=" + f.feilkode));
          }
      });
}
